// ─── orderUtils.js — pure helpers + constants for the Orders page ────────────

// 15-color palette used by every order. New orders get the next unused color.
export const ORDER_PALETTE = [
  "#FF3B30","#FF9500","#FFCC00","#34C759","#00C7BE",
  "#8b5cf6","#5E5CE6","#BF5AF2","#FF2D55","#30D158",
  "#64D2FF","#FFD60A","#FF6961","#4CD964","#007AFF",
];

// 30 shipping/logistics emoji icons.
export const ORDER_ICONS = [
  "🚢","🛳️","⛴️","⚓","🚤","🛥️",
  "🚛","🚜","🏗️","🚂","🚁","✈️",
  "📦","📫","🎁","🗃️","🏭","🏪",
  "🔩","⚙️","🧰","🔧","🔐","⚖️",
  "📋","💼","🌐","🗺️","🛒","💰",
];

// Pick the first color in the palette that no existing order is using.
// Once all colors are used, cycle back to the first.
export function nextUnusedColor(orders, palette) {
  if (!Array.isArray(palette) || palette.length === 0) return null;
  const used = new Set((orders || []).map(o => o?.color).filter(Boolean));
  for (const c of palette) {
    if (!used.has(c)) return c;
  }
  return palette[0];
}

// Generate the next ORD-NNN number based on the highest existing one.
export function nextOrderNumber(orders) {
  const nums = (orders || [])
    .map(o => {
      const m = (o?.number || "").match(/ORD-(\d+)/i);
      return m ? parseInt(m[1], 10) : 0;
    })
    .filter(n => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return `ORD-${String(max + 1).padStart(3, "0")}`;
}

// Parse a "DD.MM.YYYY" string to a Date, or null.
function parseDate(s) {
  if (!s || typeof s !== "string") return null;
  const parts = s.split(".").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return new Date(parts[2], parts[1] - 1, parts[0]);
}

// Compute the effective lead time for an order in days.
//   - If order.leadTimeDays > 0, return it (user override always wins)
//   - Else compute (earliest arrivalDate − orderDate) across batches
//   - Else compute from inStockDate if no arrivalDate
//   - Returns null if not enough data (caller can fall back to a default)
export function getEffectiveLeadTime(order) {
  if (!order) return null;
  // 1. User override
  const userVal = Number(order.leadTimeDays);
  if (userVal > 0) return userVal;
  // 2. Compute from orderDate → earliest arrival across batches
  const od = parseDate(order.orderDate);
  if (!od) return null;
  const batches = order.containerBatches || [];
  let earliest = null;
  for (const b of batches) {
    const ad = parseDate(b.arrivalDate) || parseDate(b.inStockDate);
    if (ad && (!earliest || ad < earliest)) earliest = ad;
  }
  if (!earliest) return null;
  const days = Math.round((earliest - od) / 86400000);
  return days > 0 ? days : null;
}

// Aggregate lead time across a list of orders.
// Returns { last, avg, min, max, count } where:
//   - last  = leadtime of the most recent order with a valid leadtime
//   - avg   = average of all valid leadtimes
//   - min/max from the same set
//   - count = number of orders that contributed
// User overrides (order.leadTimeDays) are honored.
export function aggregateLeadTimes(orders) {
  const items = [];
  let lastDate = null, lastVal = null;
  for (const o of orders || []) {
    const lt = getEffectiveLeadTime(o);
    if (lt == null) continue;
    items.push(lt);
    const od = parseDate(o.orderDate);
    if (od && (!lastDate || od > lastDate)) {
      lastDate = od;
      lastVal = lt;
    }
  }
  if (items.length === 0) return { last: null, avg: null, min: null, max: null, count: 0 };
  const sum = items.reduce((s, n) => s + n, 0);
  return {
    last: lastVal,
    avg: Math.round(sum / items.length),
    min: Math.min(...items),
    max: Math.max(...items),
    count: items.length,
  };
}
